import useSWR from 'swr';
import { ErrorResponse } from '../types/form';
import { ApiResources, createErrorResponse, getRequestHeaders, getUrl, Methods, processResponse } from './utils';

export interface ICurrentDetailsPayload {
  contactId: string;
  leadId: string;
}

export interface IContactDetailsPayload {
  contactId: string;
}

export interface IRedirectDetailsResponse {
  contactId: string;
  leadId: string;
  stipId: string;
  authId: string;
  connectionId: string;
}

export interface IMetadataResponse {
  metaData: string;
}

export interface ICustomerPortalDocumentsUploadResponse {
  contactId: string;
  leadId: string;
  isSuccess: boolean;
  isDocumentsUploaded: boolean;
  isReferenceAdded: boolean;
  stipItems: [
    {
      stipTypeName: string;
      relatedToEntity: string;
      receivedDate: string;
      isReceived: boolean;
    },
  ];
}

export const customerPortalRedirectDetails = async (
  currentDetails: ICurrentDetailsPayload
): Promise<IRedirectDetailsResponse | ErrorResponse> => {
  const endpoint = `${getUrl(ApiResources.customerPortalRedirect)}`;
  const request = {
    method: Methods.post,
    headers: await getRequestHeaders(),
    body: JSON.stringify(currentDetails),
  };
  try {
    const jsonResponse = await fetch(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const customerPortalMetadata = async (
  contactDetails: IContactDetailsPayload
): Promise<IMetadataResponse | ErrorResponse> => {
  const endpoint = `${getUrl(ApiResources.customerPortalMetadata)}`;
  const request = {
    method: Methods.post,
    headers: await getRequestHeaders(),
    body: JSON.stringify(contactDetails),
  };
  try {
    const jsonResponse = await fetch(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const fetchCustomerPortalUploadStatus = async (
  contactId: string,
  leadId: string
): Promise<ICustomerPortalDocumentsUploadResponse | ErrorResponse> => {
  const endpoint = `${getUrl(ApiResources.customerPortalDocuments)}`;
  const request = {
    method: Methods.post,
    headers: await getRequestHeaders(),
    body: JSON.stringify({ contactId: contactId, leadId: leadId }),
  };
  try {
    const jsonResponse = await fetch(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const useCustomerPortalDocumentsUploadStatus = (contactId: string, leadId: string) => {
  // Create a unique key
  const swrKey = contactId && leadId ? ['customerPortalDocumentsUploadStatus', contactId, leadId] : null;

  return useSWR(
    swrKey,
    async () => {
      if (!contactId || !leadId) {
        throw new Error('Contact ID and Lead ID are required');
      }
      return fetchCustomerPortalUploadStatus(contactId, leadId);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    }
  );
};

export const validateCustomerPortalRedirect = async (authId: string): Promise<ICustomerPortalDocumentsUploadResponse | ErrorResponse> => {
  const endpoint = `${getUrl(ApiResources.validateCustomerPortalRedirect)}/${authId}`;
  const request = {
    method: Methods.post,
    headers: await getRequestHeaders(),
  };
  try {
    const jsonResponse = await fetch(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};
